'use client';

import { hasDisplayableRoomTitle } from '@schuelerhilfe/shared/src/franchise/rooms';
import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import Image from 'next/image';
import { WIDGET_SPACING_CLASS } from '../spacing';
import type { RoomsGalleryResolved, TextAlignment } from './types';
import { Carousel } from '@/components/Carousel';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';
import { Text } from '@/lib/render/Text';

const ALIGN_CLASS: Record<TextAlignment, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const PER_VIEW = { '1': 1, '3': 3 } as const;

const dotLabels = (total: number, perView: number) =>
  Array.from(
    { length: Math.ceil(total / perView) },
    (_, index) => `Zu Seite ${index + 1}`,
  );

/**
 * "Unsere Räumlichkeiten" — the Standort's own room photos.
 *
 * Two authors share this widget (spec 10.03.02.01.19-Location-Facility-Gallery): the editor
 * writes the surrounding copy on the page — kicker, heading, rich text — and
 * the Franchise-Partner writes the rooms and, optionally, one line of their
 * own in the Partner-Portal. That line is body copy, not a heading: it sits
 * last in the block, directly above the photos it introduces, so the editor's
 * brand-wide heading still stands and a Standort can still speak for itself.
 *
 * Renders nothing off a location page, and nothing for a Standort with no
 * rooms — the surrounding copy alone would announce a gallery that isn't there.
 *
 * Carries the `spacing` knob although CLAUDE.md § Widget Spacing asks for a
 * reason: the widget is a section of its own, placed on brand-wide templates
 * whose neighbours differ per page, and it was specified with the Text Content
 * Widget's field set (see 10.03.02.01.19-Location-Facility-Gallery).
 */
export const RoomsGalleryComponent = ({
  resolved,
}: {
  resolved: RoomsGalleryResolved;
}) => {
  const { entities, imageUrl } = useRenderContext();
  const { kicker, title, body, alignment, columns, spacing, anchor } = resolved;

  const websiteData = entities.location?.websiteData;
  // `rooms` arrives already filtered by SH (disabled rooms are dropped
  // upstream); the `disabled` guard is for a row that predates that filter.
  const rooms = (websiteData?.rooms ?? []).filter((room) => !room.disabled);

  if (rooms.length === 0) {
    return null;
  }

  const partnerIntro = websiteData?.roomsHeadline?.trim();
  const alignClass = ALIGN_CLASS[alignment];
  const perView = PER_VIEW[columns] ?? 3;

  const slides = rooms.map((room) => {
    const src = room.image?.url ? imageUrl({ url: room.image.url }) : null;
    // The title is optional in the Partner-Portal, and SH will not store a
    // blank one — an unnamed room arrives carrying `ROOM_TITLE_PLACEHOLDER`.
    // See `hasDisplayableRoomTitle` for why the check is wider than `=== '-'`.
    const title = hasDisplayableRoomTitle(room.title) ? room.title : null;
    return (
      <figure key={room.id} className="flex flex-col items-center text-center">
        {src ? (
          <Image
            src={src}
            // SH generates the alt text from the room and its Standort; the
            // title is the fallback for a photo uploaded before it did — but
            // only a title worth showing. An empty `alt` marks the photo
            // decorative, which beats a screen reader announcing "dash".
            alt={room.image?.altText || title || ''}
            width={400}
            height={400}
            className="aspect-square w-full rounded-lg object-cover"
          />
        ) : (
          <div className="bg-secondary-lighter aspect-square w-full rounded-lg" />
        )}
        {/* Title only. The partner also maintains a description — SH requires
            one — but it is portal-side copy and is deliberately not shown
            here (spec 10.03.02.01.19-Location-Facility-Gallery).

            Now that it stands alone it carries the caption on its own, so it
            is sized up from body copy but deliberately not bolded — the weight
            belongs to the section heading above. `text-balance` keeps a two-line title
            from leaving one orphaned word, and `hyphens-auto` + `break-words`
            stop a long German compound from pushing past the card — the
            title may be up to 255 characters.

            The whole `<figcaption>` goes rather than rendering an empty one:
            its `pt-4` would otherwise leave a gap under the photo that no
            visible text explains.

            The gap under the photo is top padding here, not bottom margin on
            the image — CLAUDE.md § Widget Spacing. */}
        {title && (
          <figcaption className="w-full px-2 pt-4">
            <p className="text-base text-balance break-words hyphens-auto md:text-lg">
              {title}
            </p>
          </figcaption>
        )}
      </figure>
    );
  });

  const desktopOverflows = rooms.length > perView;
  const mobileOverflows = rooms.length > 1;

  return (
    <section
      id={anchor}
      className={classNames('scroll-mt-20', WIDGET_SPACING_CLASS[spacing])}
    >
      <div>
        {(kicker || title || (body && body.length > 0) || partnerIntro) && (
          <div className="space-y-2">
            {kicker && (
              <Text
                as="p"
                field="kicker"
                className={classNames(
                  'text-primary text-sm font-bold tracking-[0.10em] uppercase md:text-base',
                  alignClass,
                )}
              >
                {kicker}
              </Text>
            )}
            {title && (
              <Text as="h2" field="title" className={alignClass}>
                {title}
              </Text>
            )}
            {body && body.length > 0 && (
              <div
                className={classNames(
                  alignClass,
                  '[&_a]:text-primary [&_a]:underline',
                )}
              >
                <RichText nodes={body} />
              </div>
            )}
            {/* The partner's own line, last and closest to the photos. Plain
                text from the Standort rather than a `<Text>`: it carries no
                `{…}` placeholders and has no inline-edit target — it is
                written in the Partner-Portal, not the CMS. */}
            {partnerIntro && <p className={alignClass}>{partnerIntro}</p>}
          </div>
        )}

        {/* The gap under the copy is top padding on the strip, not bottom
            padding on the copy: the copy is optional, the strip is not (the
            widget renders nothing without rooms), so the part that is always
            there owns the gap — CLAUDE.md § Widget Spacing.

            `slidesPerView` is static, so desktop and mobile are two instances
            behind CSS visibility — the established pattern, see
            FlexibleCarouselComponent. */}
        <div className="hidden pt-4 md:block">
          <Carousel
            slides={slides}
            slidesPerView={perView}
            showArrows={desktopOverflows}
            showDots={desktopOverflows}
            slideClassName="px-3"
            // Fewer rooms than fit in a view is a static strip, not something
            // to scroll: centre it rather than leaving all the spare width on
            // the right. Once it scrolls, `start` keeps every page lining up.
            slideAlignment={rooms.length < perView ? 'center' : 'start'}
            prevLabel="Vorheriger Raum"
            nextLabel="Nächster Raum"
            dotLabels={dotLabels(rooms.length, perView)}
          />
        </div>
        <div className="pt-4 md:hidden">
          <Carousel
            slides={slides}
            slidesPerView={1}
            showArrows={mobileOverflows}
            showDots={mobileOverflows}
            slideClassName="px-3"
            slideAlignment="start"
            prevLabel="Vorheriger Raum"
            nextLabel="Nächster Raum"
            dotLabels={dotLabels(rooms.length, 1)}
          />
        </div>
      </div>
    </section>
  );
};
